import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService } from "@/lib/supabase";
import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Trans, useTranslation } from "react-i18next";

type LoginFormData = {
  email: string;
  password: string;
};

export const Route = createFileRoute("/login")({
  component: Login,
  beforeLoad: ({ context }) => {
    if (
      context.authentication &&
      context.authentication.session &&
      context.authentication.user?.currentProjectId
    ) {
      throw redirect({
        to: "/$projectId",
        params: { projectId: context.authentication.user.currentProjectId },
      });
    } else if (context.authentication && context.authentication.session) {
      throw redirect({
        to: "/project-create-or-join",
      });
    }
  },
});

function Login() {
  const { t } = useTranslation();
  const router = useRouter();
  const { authentication } = Route.useRouteContext();

  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    try {
      const result = await authService.signIn({
        email: data.email,
        password: data.password,
      });
      if (result.error) {
        setError("root", {
          type: "manual",
          message: result.error.message,
        });

        return;
      }

      if (result.session && result.user && authentication) {
        authentication.setUser(result.user);

        if (result.user.currentProjectId) {
          router.navigate({
            to: "/$projectId",
            params: { projectId: result.user.currentProjectId },
            replace: true,
          });
        } else {
          router.navigate({ to: "/project-create-or-join", replace: true });
        }
      }
    } catch {
      setError("root", {
        type: "manual",
        message: t("login.unexpectedError", {
          defaultValue: "An unexpected error occurred",
        }),
      });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await authService.signInWithOAuth({
        provider: "google",
        redirectTo: `${window.location.origin}/google-callback`,
      });

      if (result.error) {
        toast.error(
          t("login.failedToInitiateGoogleLogin", {
            defaultValue: "Failed to initiate Google login",
          }),
        );
      } else if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error("Google login error:", error);
      toast.error(
        t("login.googleLoginError", {
          defaultValue: "An error occurred during Google login",
        }),
      );
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      toast.error(
        t("login.pleaseEnterEmail", {
          defaultValue: "Please enter your email",
        }),
      );
      return;
    }

    setIsResetting(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password-by-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            email: resetEmail,
            redirectTo: `${window.location.origin}/change-password`,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
            t("login.failedToSendResetEmail", {
              defaultValue: "Failed to send reset email",
            }),
        );
      }

      toast.success(
        t("login.passwordResetEmailSent", {
          defaultValue: "Password reset email sent",
        }),
        {
          description: t("login.checkInbox", {
            defaultValue: "Please check your inbox.",
          }),
        },
      );
      setShowResetDialog(false);
      setResetEmail("");
    } catch (error) {
      console.error("Error sending reset email:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("login.failedToSendResetEmail", {
              defaultValue: "Failed to send reset email",
            }),
      );
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>
                <Trans i18nKey={"login.title"}>Login to your account</Trans>
              </CardTitle>
              <CardDescription>
                <Trans i18nKey={"login.description"}>
                  Enter your email below to login to your account
                </Trans>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-3">
                    <Label htmlFor="email">
                      <Trans i18nKey={"login.email"}>Email</Trans>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t("login.emailPlaceholder", {
                        defaultValue: "m@example.com",
                      })}
                      {...register("email", {
                        required: t("login.emailRequired", {
                          defaultValue: "Email is required",
                        }),
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: t("login.invalidEmail", {
                            defaultValue: "Invalid email address",
                          }),
                        },
                      })}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-600">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-3">
                    <div className="flex items-center">
                      <Label htmlFor="password">
                        <Trans i18nKey={"login.password"}>Password</Trans>
                      </Label>
                      <button
                        type="button"
                        onClick={() => setShowResetDialog(true)}
                        className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                      >
                        <Trans i18nKey={"login.forgotPassword"}>
                          Forgot your password?
                        </Trans>
                      </button>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      {...register("password", {
                        required: t("login.passwordRequired", {
                          defaultValue: "Password is required",
                        }),
                        minLength: {
                          value: 6,
                          message: t("login.passwordMinLength", {
                            defaultValue:
                              "Password must be at least 6 characters",
                          }),
                        },
                      })}
                    />
                    {errors.password && (
                      <p className="text-sm text-red-600">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                  {errors.root && (
                    <p className="text-sm text-red-600">
                      {errors.root.message}
                    </p>
                  )}
                  <div className="flex flex-col gap-3">
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t("login.loggingIn", {
                            defaultValue: "Logging in...",
                          })
                        : t("login.loginButton", {
                            defaultValue: "Login",
                          })}
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <Separator className="w-full" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          <Trans i18nKey={"login.or"}>OR </Trans>
                        </span>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={handleGoogleLogin}
                    >
                      <svg
                        className="mr-2 h-4 w-4"
                        aria-hidden="true"
                        focusable="false"
                        data-prefix="fab"
                        data-icon="google"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 488 512"
                      >
                        <path
                          fill="currentColor"
                          d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
                        />
                      </svg>
                      <Trans i18nKey={"login.continueWithGoogle"}>
                        Continue with Google
                      </Trans>
                    </Button>
                  </div>
                </div>
                <div className="mt-4 text-center text-sm">
                  <Trans i18nKey={"login.noAccount"}>
                    Don&apos;t have an account?
                    <Link
                      to={"/signup"}
                      className="underline underline-offset-4"
                    >
                      Sign up
                    </Link>
                  </Trans>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="fixed top-6 left-5">
          <Button variant="link" onClick={() => router.navigate({ to: "/" })}>
            <ArrowLeft />
            <Trans i18nKey={"login.goBack"}>Go back</Trans>
          </Button>
        </div>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Trans i18nKey={"login.resetPassword"}>Reset Password</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans i18nKey={"login.resetPasswordDescription"}>
                Enter your email address and we'll send you a link to reset your
                password.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">
                <Trans i18nKey={"login.email"}>Email</Trans>
              </Label>
              <Input
                id="reset-email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder={t("login.emailPlaceholder", {
                  defaultValue: "m@example.com",
                })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowResetDialog(false);
                setResetEmail("");
              }}
              disabled={isResetting}
            >
              <Trans i18nKey={"login.cancel"}>Cancel</Trans>
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={isResetting || !resetEmail}
            >
              {isResetting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Trans i18nKey={"login.sendResetEmail"}>Send Reset Email</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
